import React from 'react';
import { Link } from 'react-router-dom';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';

const matrixSections = [
  {
    id: 'identity-proofing',
    letter: 'A',
    title: 'Identity proofing',
    description: 'Draft view of how current identity capture, verification, and review components map to a future FAS/CSAM-style evidence path.',
    rows: [
      {
        area: 'Identity proofing',
        requirement: 'ID document capture',
        component: 'Verification session intake + document capture UI',
        evidenceTo: '/internal/bosa-evidence-pack',
        evidenceLabel: 'BOSA evidence pack',
        status: 'Implemented',
        nextAction: 'Keep capture scoped to internal verification flows and preserve evidence boundaries.',
        risk: 'Low',
      },
      {
        area: 'Identity proofing',
        requirement: 'Selfie / face match',
        component: 'Python verifier face-match pipeline',
        evidenceTo: '/internal/bosa-evidence-pack',
        evidenceLabel: 'BOSA evidence pack',
        status: 'Partial',
        nextAction: 'Document reviewer thresholds, false-match handling, and override policy.',
        risk: 'Medium',
      },
      {
        area: 'Identity proofing',
        requirement: 'MRZ / OCR extraction',
        component: 'OCR and MRZ extraction in the verification service',
        evidenceTo: '/internal/bosa-evidence-pack',
        evidenceLabel: 'BOSA evidence pack',
        status: 'Implemented',
        nextAction: 'Keep extraction outputs minimized and avoid exposing raw OCR payloads.',
        risk: 'Low',
      },
      {
        area: 'Identity proofing',
        requirement: 'Manual review',
        component: 'Identity detail review workflow',
        evidenceTo: '/internal/identities',
        evidenceLabel: 'Identity operations',
        status: 'Partial',
        nextAction: 'Define reviewer queue, escalation path, and second-pair review policy.',
        risk: 'Medium',
      },
      {
        area: 'Identity proofing',
        requirement: 'Evidence retention policy',
        component: 'Audit governance notes and retention guidance',
        evidenceTo: '/internal/audit-governance-pack',
        evidenceLabel: 'Audit governance',
        status: 'Draft',
        nextAction: 'Write a retention matrix and confirm deletion windows before any external discussion.',
        risk: 'High',
      },
      {
        area: 'Identity proofing',
        requirement: 'Onboarding verification status',
        component: 'Internal operations dashboard + onboarding monitoring',
        evidenceTo: '/internal/partner-technical-readiness',
        evidenceLabel: 'Partner readiness',
        status: 'Implemented',
        nextAction: 'Keep the dashboard limited to internal operational metadata only.',
        risk: 'Low',
      },
    ],
  },
  {
    id: 'auth-digital-key',
    letter: 'B',
    title: 'Authentication / digital key model',
    description: 'Current authentication and device-binding work that could support a future digital key recognition path, without claiming an official scheme.',
    rows: [
      {
        area: 'Authentication / digital key model',
        requirement: 'User account',
        component: 'Authenticated workspace user model',
        evidenceTo: '/internal/digital-key-architecture',
        evidenceLabel: 'Digital key architecture',
        status: 'Implemented',
        nextAction: 'Continue deriving access from live DB state rather than stale token claims.',
        risk: 'Low',
      },
      {
        area: 'Authentication / digital key model',
        requirement: 'Login request lifecycle',
        component: 'Authenticator preview + OIDC lab request flow',
        evidenceTo: '/internal/authenticator-preview',
        evidenceLabel: 'Authenticator preview',
        status: 'Partial',
        nextAction: 'Document lifecycle states and rejection paths for internal testing only.',
        risk: 'Medium',
      },
      {
        area: 'Authentication / digital key model',
        requirement: 'Authenticator preview',
        component: 'Internal authenticator preview',
        evidenceTo: '/internal/authenticator-preview',
        evidenceLabel: 'Authenticator preview',
        status: 'Implemented',
        nextAction: 'Keep the preview isolated from normal workspace routes and public login.',
        risk: 'Low',
      },
      {
        area: 'Authentication / digital key model',
        requirement: 'Consent screen',
        component: 'Relying party profile and demo consent patterns',
        evidenceTo: '/internal/relying-party-profiles',
        evidenceLabel: 'Relying party profiles',
        status: 'Partial',
        nextAction: 'Define a separate user consent model before any broader protocol work.',
        risk: 'Medium',
      },
      {
        area: 'Authentication / digital key model',
        requirement: 'Session / token security',
        component: 'JWT-authenticated API sessions',
        evidenceTo: '/internal/digital-key-architecture',
        evidenceLabel: 'Digital key architecture',
        status: 'Implemented',
        nextAction: 'Keep platform-admin checks separate from workspace authorization.',
        risk: 'Low',
      },
      {
        area: 'Authentication / digital key model',
        requirement: 'Replay protection',
        component: 'OIDC lab request lifecycle and callback simulation',
        evidenceTo: '/internal/oidc-lab',
        evidenceLabel: 'OIDC lab',
        status: 'Draft',
        nextAction: 'Add a documented nonce/state story before any future external protocol discussion.',
        risk: 'High',
      },
      {
        area: 'Authentication / digital key model',
        requirement: 'Device / IP signals',
        component: 'Safe approval-context snapshot and device-bound metadata',
        evidenceTo: '/internal/authenticator-preview',
        evidenceLabel: 'Authenticator preview',
        status: 'Partial',
        nextAction: 'Decide which signals are acceptable evidence candidates and which stay internal only.',
        risk: 'Medium',
      },
    ],
  },
  {
    id: 'lifecycle-management',
    letter: 'C',
    title: 'Lifecycle management',
    description: 'Identity lifecycle controls needed for any regulated identity or digital-key path, kept as internal operational capabilities only.',
    rows: [
      {
        area: 'Lifecycle management',
        requirement: 'Create identity',
        component: 'Identity operations and verification-linked records',
        evidenceTo: '/internal/identities',
        evidenceLabel: 'Identity operations',
        status: 'Implemented',
        nextAction: 'Keep identity creation tied to an approved verification outcome.',
        risk: 'Low',
      },
      {
        area: 'Lifecycle management',
        requirement: 'Suspend identity',
        component: 'Internal moderation and admin decision flow',
        evidenceTo: '/internal/identities',
        evidenceLabel: 'Identity operations',
        status: 'Draft',
        nextAction: 'Define the suspend state, reason capture, and follow-up review process.',
        risk: 'High',
      },
      {
        area: 'Lifecycle management',
        requirement: 'Reactivate identity',
        component: 'Internal moderation and admin decision flow',
        evidenceTo: '/internal/identities',
        evidenceLabel: 'Identity operations',
        status: 'Not started',
        nextAction: 'Document an approval path before enabling reactivation in any workflow.',
        risk: 'High',
      },
      {
        area: 'Lifecycle management',
        requirement: 'Revoke identity',
        component: 'Identity event and status history',
        evidenceTo: '/internal/audit-governance-pack',
        evidenceLabel: 'Audit governance',
        status: 'Partial',
        nextAction: 'Define revocation authority, effective time, and downstream notification handling.',
        risk: 'High',
      },
      {
        area: 'Lifecycle management',
        requirement: 'Event timeline',
        component: 'Identity detail event timeline',
        evidenceTo: '/internal/identities',
        evidenceLabel: 'Identity operations',
        status: 'Implemented',
        nextAction: 'Keep event history readable and avoid exposing raw evidence payloads.',
        risk: 'Low',
      },
      {
        area: 'Lifecycle management',
        requirement: 'Reason capture',
        component: 'Moderation notes and review reason fields',
        evidenceTo: '/internal/audit-governance-pack',
        evidenceLabel: 'Audit governance',
        status: 'Partial',
        nextAction: 'Standardize reason codes for operational use and future audit review.',
        risk: 'Medium',
      },
    ],
  },
  {
    id: 'audit-governance',
    letter: 'D',
    title: 'Audit & governance',
    description: 'Internal controls that help track evidence, access, and change history without exposing official compliance claims.',
    rows: [
      {
        area: 'Audit & governance',
        requirement: 'Audit logs',
        component: 'Identity event timeline + audit governance pack',
        evidenceTo: '/internal/audit-governance-pack',
        evidenceLabel: 'Audit governance',
        status: 'Implemented',
        nextAction: 'Keep audit output minimized and focused on internal operational metadata.',
        risk: 'Low',
      },
      {
        area: 'Audit & governance',
        requirement: 'Admin access controls',
        component: 'PlatformAdminOnly + requirePlatformAdmin',
        evidenceTo: '/internal/audit-governance-pack',
        evidenceLabel: 'Audit governance',
        status: 'Implemented',
        nextAction: 'Preserve strict separation between internal admin and normal workspace access.',
        risk: 'Low',
      },
      {
        area: 'Audit & governance',
        requirement: 'Platform-admin-only area',
        component: 'Internal portal split under /internal/*',
        evidenceTo: '/internal/audit-governance-pack',
        evidenceLabel: 'Audit governance',
        status: 'Implemented',
        nextAction: 'Keep normal workspace navigation and APIs separate from internal admin routes.',
        risk: 'Low',
      },
      {
        area: 'Audit & governance',
        requirement: 'Evidence pack',
        component: 'BOSA evidence pack and internal readiness notes',
        evidenceTo: '/internal/bosa-evidence-pack',
        evidenceLabel: 'BOSA evidence pack',
        status: 'Partial',
        nextAction: 'Tighten the evidence index and separate draft items from validated items.',
        risk: 'Medium',
      },
      {
        area: 'Audit & governance',
        requirement: 'Incident response placeholder',
        component: 'Internal ops notes only',
        evidenceTo: '/internal/audit-governance-pack',
        evidenceLabel: 'Audit governance',
        status: 'Not started',
        nextAction: 'Add a minimal runbook placeholder before any live readiness discussion.',
        risk: 'Medium',
      },
      {
        area: 'Audit & governance',
        requirement: 'Change history',
        component: 'Page history and evidence pack updates',
        evidenceTo: '/internal/audit-governance-pack',
        evidenceLabel: 'Audit governance',
        status: 'Draft',
        nextAction: 'Document which page sections are stable versus still in draft form.',
        risk: 'Medium',
      },
    ],
  },
  {
    id: 'privacy-minimization',
    letter: 'E',
    title: 'Privacy & data minimization',
    description: 'Guardrails for keeping the internal readiness work privacy-aware and scoped to minimized operational data.',
    rows: [
      {
        area: 'Privacy & data minimization',
        requirement: 'No raw evidence exposed in internal overview',
        component: 'Admin overview endpoint + internal dashboard summaries',
        evidenceTo: '/internal/audit-governance-pack',
        evidenceLabel: 'Audit governance',
        status: 'Implemented',
        nextAction: 'Keep the overview limited to counts, timestamps, and safe summaries.',
        risk: 'Low',
      },
      {
        area: 'Privacy & data minimization',
        requirement: 'No RRN/BIS handling in current scope',
        component: 'Regulated identifiers page and scoped internal notes',
        evidenceTo: '/internal/regulated-identifiers',
        evidenceLabel: 'Regulated identifiers',
        status: 'Out of scope for MVP',
        nextAction: 'Do not introduce regulated identifier handling until the scope is explicitly approved.',
        risk: 'Low',
      },
      {
        area: 'Privacy & data minimization',
        requirement: 'Image retention settings',
        component: 'Retention policy placeholders',
        evidenceTo: '/internal/audit-governance-pack',
        evidenceLabel: 'Audit governance',
        status: 'Draft',
        nextAction: 'Define retention settings before any broader data governance discussion.',
        risk: 'High',
      },
      {
        area: 'Privacy & data minimization',
        requirement: 'GDPR / DPIA placeholder',
        component: 'Privacy review notes',
        evidenceTo: '/internal/audit-governance-pack',
        evidenceLabel: 'Audit governance',
        status: 'Draft',
        nextAction: 'Prepare a placeholder review template for internal planning only.',
        risk: 'High',
      },
      {
        area: 'Privacy & data minimization',
        requirement: 'Data minimization notes',
        component: 'Safe field selection in internal APIs and pages',
        evidenceTo: '/internal/bosa-evidence-pack',
        evidenceLabel: 'BOSA evidence pack',
        status: 'Needs validation',
        nextAction: 'Validate that all readiness views remain free of raw evidence, tokens, and secrets.',
        risk: 'Medium',
      },
    ],
  },
  {
    id: 'technical-integration',
    letter: 'F',
    title: 'Technical integration readiness',
    description: 'Internal tooling that helps simulate protocol and relying-party readiness without creating a public integration surface.',
    rows: [
      {
        area: 'Technical integration readiness',
        requirement: 'OIDC Lab',
        component: 'Private OIDC lab',
        evidenceTo: '/internal/oidc-lab',
        evidenceLabel: 'OIDC lab',
        status: 'Implemented',
        nextAction: 'Keep the lab internal-only and isolated from production protocol surfaces.',
        risk: 'Low',
      },
      {
        area: 'Technical integration readiness',
        requirement: 'Relying party profiles',
        component: 'Internal relying party profile management',
        evidenceTo: '/internal/relying-party-profiles',
        evidenceLabel: 'Relying party profiles',
        status: 'Implemented',
        nextAction: 'Preserve profile-level isolation and admin-only access.',
        risk: 'Low',
      },
      {
        area: 'Technical integration readiness',
        requirement: 'Redirect / callback simulation',
        component: 'OIDC lab callback flow',
        evidenceTo: '/internal/oidc-lab',
        evidenceLabel: 'OIDC lab',
        status: 'Partial',
        nextAction: 'Document redirect and callback behavior for internal testing only.',
        risk: 'Medium',
      },
      {
        area: 'Technical integration readiness',
        requirement: 'Internal authenticator preview',
        component: 'Authenticator preview and approval context',
        evidenceTo: '/internal/authenticator-preview',
        evidenceLabel: 'Authenticator preview',
        status: 'Implemented',
        nextAction: 'Keep the preview’s safe-approval context separate from real production flows.',
        risk: 'Low',
      },
      {
        area: 'Technical integration readiness',
        requirement: 'API route boundaries',
        component: 'Strict backend mount prefixes and router separation',
        evidenceTo: '/internal/digital-key-architecture',
        evidenceLabel: 'Digital key architecture',
        status: 'Implemented',
        nextAction: 'Preserve workspace routes outside platform-admin middleware.',
        risk: 'Low',
      },
      {
        area: 'Technical integration readiness',
        requirement: 'Platform-admin-only APIs',
        component: '/api/admin/*, /api/oidc-lab/*, /api/digital-key-accounts/*, /api/idem-identities/*',
        evidenceTo: '/internal/audit-governance-pack',
        evidenceLabel: 'Audit governance',
        status: 'Implemented',
        nextAction: 'Keep all internal APIs behind explicit platform-admin checks.',
        risk: 'Low',
      },
    ],
  },
  {
    id: 'operations-readiness',
    letter: 'G',
    title: 'Operations readiness',
    description: 'Operational controls for internal monitoring, support, and review workflows while the readiness work remains draft-only.',
    rows: [
      {
        area: 'Operations readiness',
        requirement: 'Internal operations dashboard',
        component: 'Internal admin landing page',
        evidenceTo: '/internal',
        evidenceLabel: 'Internal dashboard',
        status: 'Implemented',
        nextAction: 'Keep the overview focused on internal operational metadata and summaries only.',
        risk: 'Low',
      },
      {
        area: 'Operations readiness',
        requirement: 'Onboarding monitoring',
        component: 'Internal overview + onboarding status summaries',
        evidenceTo: '/internal/partner-technical-readiness',
        evidenceLabel: 'Partner readiness',
        status: 'Implemented',
        nextAction: 'Keep onboarding monitoring tied to safe operational counts, not raw evidence.',
        risk: 'Low',
      },
      {
        area: 'Operations readiness',
        requirement: 'Verification review workflow',
        component: 'Identity detail review screens',
        evidenceTo: '/internal/identities',
        evidenceLabel: 'Identity operations',
        status: 'Partial',
        nextAction: 'Define reviewer assignment, queue status, and escalation handling.',
        risk: 'Medium',
      },
      {
        area: 'Operations readiness',
        requirement: 'Support / recovery placeholder',
        component: 'Internal ops notes',
        evidenceTo: '/internal/audit-governance-pack',
        evidenceLabel: 'Audit governance',
        status: 'Draft',
        nextAction: 'Create a minimal support and recovery placeholder for future internal planning.',
        risk: 'Medium',
      },
      {
        area: 'Operations readiness',
        requirement: 'Availability / monitoring placeholder',
        component: 'Internal readiness dashboard only',
        evidenceTo: '/internal/partner-technical-readiness',
        evidenceLabel: 'Partner readiness',
        status: 'Not started',
        nextAction: 'Add monitoring and availability notes when an operational plan exists.',
        risk: 'Medium',
      },
    ],
  },
];

const statusStyles = {
  Implemented: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  Partial: 'border-amber-300 bg-amber-50 text-amber-800',
  Draft: 'border-zinc-300 bg-zinc-50 text-zinc-700',
  'Needs validation': 'border-sky-300 bg-sky-50 text-sky-800',
  'Not started': 'border-stone-300 bg-stone-50 text-stone-700',
  'Out of scope for MVP': 'border-violet-300 bg-violet-50 text-violet-800',
};

const riskStyles = {
  Low: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  Medium: 'border-amber-300 bg-amber-50 text-amber-800',
  High: 'border-rose-300 bg-rose-50 text-rose-800',
};

const allRows = matrixSections.flatMap((section) => section.rows);

const summaryCounts = {
  implemented: allRows.filter((row) => row.status === 'Implemented').length,
  partialOrDraft: allRows.filter((row) => row.status === 'Partial' || row.status === 'Draft').length,
  needsValidation: allRows.filter((row) => row.status === 'Needs validation').length,
  highRiskGaps: allRows.filter((row) => row.risk === 'High' && row.status !== 'Implemented').length,
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${statusStyles[status] || statusStyles.Draft}`}>
      {status}
    </span>
  );
}

function RiskBadge({ risk }) {
  return (
    <span className={`inline-flex border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${riskStyles[risk] || riskStyles.Medium}`}>
      {risk}
    </span>
  );
}

function SummaryCard({ label, value, tone }) {
  return (
    <div className={`border p-4 ${tone}`}>
      <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="mt-2 text-3xl font-semibold tracking-tight">{value}</div>
    </div>
  );
}

function MatrixSection({ section }) {
  return (
    <section className="border border-zinc-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Section {section.letter}</p>
          <h2 className="mt-1 font-display text-xl font-semibold">{section.title}</h2>
          <p className="mt-2 max-w-4xl text-sm text-zinc-600">{section.description}</p>
        </div>
        <div className="rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-xs text-zinc-600">
          {section.rows.length} items
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-[1200px] w-full text-sm">
          <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="py-2 pr-4 font-medium">Area</th>
              <th className="py-2 pr-4 font-medium">Requirement / capability</th>
              <th className="py-2 pr-4 font-medium">Current idemGO component</th>
              <th className="py-2 pr-4 font-medium">Evidence link</th>
              <th className="py-2 pr-4 font-medium">Status</th>
              <th className="py-2 pr-4 font-medium">Next action</th>
              <th className="py-2 font-medium">Risk</th>
            </tr>
          </thead>
          <tbody>
            {section.rows.map((row) => (
              <tr key={`${section.id}-${row.requirement}`} className="border-b border-zinc-100 last:border-b-0 align-top">
                <td className="py-3 pr-4 font-medium text-zinc-800">{row.area}</td>
                <td className="py-3 pr-4 text-zinc-700">{row.requirement}</td>
                <td className="py-3 pr-4 text-zinc-600">{row.component}</td>
                <td className="py-3 pr-4">
                  <Link
                    to={row.evidenceTo}
                    className="inline-flex items-center gap-1 text-sm text-zinc-700 underline decoration-zinc-300 underline-offset-2 hover:text-zinc-900 hover:decoration-zinc-900"
                  >
                    {row.evidenceLabel}
                  </Link>
                </td>
                <td className="py-3 pr-4">
                  <StatusBadge status={row.status} />
                </td>
                <td className="py-3 pr-4 text-zinc-600">{row.nextAction}</td>
                <td className="py-3">
                  <RiskBadge risk={row.risk} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function FasReadiness() {
  const quickLinks = [
    { to: '/internal/bosa-evidence-pack', label: 'BOSA Evidence Pack' },
    { to: '/internal/audit-governance-pack', label: 'Audit Governance Pack' },
    { to: '/internal/digital-key-demo', label: 'Digital Key Demo Runner' },
    { to: '/internal/digital-key-architecture', label: 'Digital Key Architecture' },
    { to: '/internal/digital-key-accounts', label: 'Digital Key Accounts' },
    { to: '/internal/authenticator-preview', label: 'Authenticator Preview' },
    { to: '/internal/oidc-lab', label: 'OIDC Lab' },
    { to: '/internal/relying-party-profiles', label: 'Relying Party Profiles' },
    { to: '/internal/identities', label: 'Identity Operations' },
    { to: '/internal/regulated-identifiers', label: 'Regulated Identifiers' },
    { to: '/internal/partner-technical-readiness', label: 'Partner Technical Readiness' },
  ];

  return (
    <div data-testid="fas-readiness-page">
      <VerificationBreadcrumb rootLabel="Internal" rootTo="/internal" current="FAS / CSAM Readiness Matrix" showDashboard={false} />

      <div className="mb-6">
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Internal admin</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">FAS / CSAM Readiness Matrix v0.1</h1>
        <p className="mt-2 max-w-4xl text-sm text-zinc-600">
          Draft internal readiness tracker for a potential Belgian FAS / CSAM digital key recognition path. It is a planning aid only and does not claim official recognition, approval, or LoA status.
        </p>
      </div>

      <section className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <p className="font-medium">Internal-only readiness view</p>
        <p className="mt-1">This page is an internal readiness tracker. It is not an official certification, approval, or LoA statement.</p>
      </section>

      <section className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard label="Implemented capabilities" value={summaryCounts.implemented} tone="border-emerald-200 bg-emerald-50" />
        <SummaryCard label="Partial / draft capabilities" value={summaryCounts.partialOrDraft} tone="border-amber-200 bg-amber-50" />
        <SummaryCard label="Needs validation" value={summaryCounts.needsValidation} tone="border-sky-200 bg-sky-50" />
        <SummaryCard label="High-risk gaps" value={summaryCounts.highRiskGaps} tone="border-rose-200 bg-rose-50" />
      </section>

      <section className="mt-6 border border-zinc-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-lg font-semibold">How to read the matrix</h2>
            <p className="mt-1 max-w-4xl text-sm text-zinc-600">
              Each row maps a readiness area to the current idemGO component, a safe evidence pointer, the current status, the next action, and a simple risk level. The labels are internal planning labels only.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-zinc-600">
            <span className="border border-emerald-200 bg-emerald-50 px-2 py-1">Implemented</span>
            <span className="border border-amber-200 bg-amber-50 px-2 py-1">Partial</span>
            <span className="border border-zinc-200 bg-zinc-50 px-2 py-1">Draft</span>
            <span className="border border-sky-200 bg-sky-50 px-2 py-1">Needs validation</span>
            <span className="border border-stone-200 bg-stone-50 px-2 py-1">Not started</span>
            <span className="border border-violet-200 bg-violet-50 px-2 py-1">Out of scope for MVP</span>
          </div>
        </div>
      </section>

      <div className="mt-6 space-y-6">
        {matrixSections.map((section) => (
          <MatrixSection key={section.id} section={section} />
        ))}
      </div>

      <section className="mt-6 border border-zinc-200 bg-white p-5">
        <h2 className="font-display text-lg font-semibold">Related internal evidence pages</h2>
        <p className="mt-1 text-sm text-zinc-600">These are the companion internal pages the matrix points to for evidence and operational context.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {quickLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400 hover:text-zinc-900"
            >
              {link.label}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
